// Centralised blog data layer. Backed by MongoDB via `/api/blog/posts`.
// A bundled static payload lives at `@/data/blogPosts` + `blogContent` as
// a last-resort fallback for total network failure so the site is never
// blank. An empty API response is trusted (admin intentionally cleared
// posts) — do NOT fall back to static in that case, or admin deletes
// would resurrect deleted posts.
import axios from "axios";
import staticPosts from "@/data/blogPosts";
import staticContent from "@/data/blogContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert API blog post (Mongo) to the shape the UI components already use.
function fromApi(p) {
  return {
    slug: p.slug,
    title: p.title,
    date: p.date,
    category: p.category,
    tags: p.tags || [],
    excerpt: p.excerpt || "",
    image: p.image || null,
    author: p.author || "Antera Team",
    readTime: p.read_time || "",
    bodyHtml: p.body_html || "",
  };
}

let listCache = null;
let listPromise = null;

export async function fetchBlogPosts() {
  if (listCache) return listCache;
  if (listPromise) return listPromise;
  listPromise = (async () => {
    try {
      const { data } = await axios.get(`${API}/blog/posts?limit=500`, {
        timeout: 8000,
      });
      if (Array.isArray(data)) {
        // Trust the API's response — even an empty array is authoritative
        // (admin may have cleared all posts). Only fall back if the request
        // itself throws.
        listCache = data.map(fromApi);
        return listCache;
      }
    } catch (e) {
      // Network failure only — surface static payload so site isn't blank.
      console.warn("Blog API unreachable — using static fallback:", e?.message);
    }
    listCache = staticPosts.map((p) => ({ ...p, bodyHtml: staticContent[p.slug] || "" }));
    return listCache;
  })();
  return listPromise;
}

export async function fetchBlogPost(slug) {
  try {
    const { data } = await axios.get(`${API}/blog/posts/${slug}`, { timeout: 8000 });
    if (data && data.slug) return fromApi(data);
  } catch (e) {
    // Fall back to static only on network error / 404 (post was deleted?).
  }
  const local = staticPosts.find((p) => p.slug === slug);
  if (!local) return null;
  return { ...local, bodyHtml: staticContent[slug] || "" };
}
